import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Prakriya, FormButton, Variant } from "./prakriya";
import { ParadigmTable, ParadigmGrid } from "../../grammar/paradigm-table";
import { PARTICIPLES, ParticipleEntry } from "../../grammar/participle-data";
import { Gender } from "../../grammar/types";
import { DerivedForm } from "../../grammar/declension";
import { Conjugation, conjugate } from "../../grammar/conjugation";
import {
    GANA_LABEL,
    RootEntry,
    findRoots,
    loadRoots,
    toDhatu,
} from "../../grammar/roots";
import {
    LAKARAS,
    Lakara,
    PURUSHAS,
    Purusha,
    SANADIS,
    Sanadi,
    VACANAS,
    Vacana,
} from "../../grammar/verb-types";

const Cell = (props: {
    forms: DerivedForm[];
    onSelect: (f: DerivedForm) => void;
    selected?: DerivedForm;
}) => {
    if (props.forms.length === 0) return <td>&mdash;</td>;
    return (
        <td>
            {props.forms.map((f, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <Variant> / </Variant>}
                    <FormButton
                        type="button"
                        data-selected={props.selected === f}
                        onClick={() => props.onSelect(f)}
                        title="Show derivation"
                    >
                        {f.text}
                    </FormButton>
                </React.Fragment>
            ))}
        </td>
    );
};

/**
 * Conjugation of a verbal root.
 *
 * A dictionary headword can answer to more than one root — *vid* is five
 * different verbs across five gaṇas — so when that happens the reader picks,
 * rather than us guessing. Where Monier-Williams states the class, the matching
 * root is offered first.
 */
export const ConjugationWindow = React.forwardRef(
    (props: {
        word: string;
        verbClasses?: string;
        /** Set when the same headword also declines as a nominal. */
        onDecline?: () => void;
        onClose: () => void;
    }, ref) => {
        const [roots, setRoots] = useState<RootEntry[] | null>(null);
        const [rootIndex, setRootIndex] = useState(0);
        const [lakara, setLakara] = useState<Lakara>("Lat");
        // A participle is a kṛdanta that then declines, so it replaces the
        // conjugation table rather than sitting beside it.
        const [participle, setParticiple] = useState<ParticipleEntry | null>(null);
        // Causative, desiderative, intensive: these build a new verb from the
        // root, so the whole table changes rather than one cell.
        const [sanadi, setSanadi] = useState<Sanadi | null>(null);
        const [table, setTable] = useState<Conjugation | null>(null);
        const [selected, setSelected] = useState<DerivedForm | undefined>(undefined);

        // The root index is ~217 KB and only fetched when a verb is opened.
        useEffect(() => {
            let cancelled = false;
            loadRoots().then(() => {
                if (cancelled) return;
                setRoots(findRoots(props.word, props.verbClasses));
                setRootIndex(0);
            });
            return () => { cancelled = true; };
        }, [props.word, props.verbClasses]);

        useEffect(() => {
            if (!roots || roots.length === 0) return;
            let cancelled = false;
            setTable(null);
            setSelected(undefined);
            const base = toDhatu(roots[rootIndex]);
            conjugate({ ...base, sanadi: sanadi ? [sanadi] : [] }, lakara)
                .then(c => { if (!cancelled) setTable(c); })
                .catch(() => undefined);
            return () => { cancelled = true; };
        }, [roots, rootIndex, lakara, sanadi]);

        const current = roots && roots.length > 0 ? roots[rootIndex] : null;

        return (
            <StyledWindow ref={ref as React.Ref<HTMLDivElement>}>
                <CloseButton onClick={props.onClose} className="icon-cancel" />
                <Word>&radic;{props.word}</Word>

                {roots && roots.length === 0 && (
                    <Empty>This word is not in the Dhātupāṭha, so it cannot be conjugated.</Empty>
                )}

                {roots && roots.length > 1 && (
                    <RootChoice>
                        {roots.map((r, i) => (
                            <RootButton
                                key={r.code}
                                type="button"
                                data-selected={i === rootIndex}
                                onClick={() => setRootIndex(i)}
                                title={r.artha}
                            >
                                {GANA_LABEL[r.gana]}
                            </RootButton>
                        ))}
                    </RootChoice>
                )}

                {current && (
                    <>
                        <LakaraChoice>
                            {LAKARAS.map(l => (
                                <LakaraButton
                                    key={l.id}
                                    type="button"
                                    data-selected={l.id === lakara && !participle}
                                    onClick={() => { setParticiple(null); setLakara(l.id); }}
                                    title={l.gloss}
                                >
                                    {l.name}
                                </LakaraButton>
                            ))}
                        </LakaraChoice>

                        <LakaraChoice>
                            {SANADIS.map(sa => (
                                <LakaraButton
                                    key={sa.id}
                                    type="button"
                                    data-selected={sanadi === sa.id}
                                    onClick={() => setSanadi(sanadi === sa.id ? null : sa.id)}
                                    title={sa.gloss}
                                >
                                    {sa.name}
                                </LakaraButton>
                            ))}
                        </LakaraChoice>

                        <LakaraChoice>
                            {PARTICIPLES.map(p => (
                                <LakaraButton
                                    key={p.krt}
                                    type="button"
                                    data-selected={participle?.krt === p.krt}
                                    onClick={() => setParticiple(participle?.krt === p.krt ? null : p)}
                                    title={p.gloss}
                                >
                                    {p.name}
                                </LakaraButton>
                            ))}
                        </LakaraChoice>

                        {participle && (
                            <ParadigmGrid>
                                {(["mas", "fem", "neu"] as Gender[]).map(g => (
                                    <ParadigmTable
                                        key={g}
                                        analysis={{
                                            kind: "krdanta",
                                            root: current.aupadeshika,
                                            gana: current.gana,
                                            krt: participle.krt,
                                        }}
                                        gender={g}
                                        caption={`${participle.name} — ${g}`}
                                    />
                                ))}
                            </ParadigmGrid>
                        )}

                        {!participle && !table && <Loading>…</Loading>}

                        {!participle && table && table.empty && (
                            <Empty>
                                This root does not form the{" "}
                                {LAKARAS.find(l => l.id === lakara)?.gloss}.
                            </Empty>
                        )}

                        {!participle && table && !table.empty && (
                            <StyledTable>
                                <tbody>
                                    <tr>
                                        <THead />
                                        {VACANAS.map(v => <THead key={v.id}>{v.gloss}</THead>)}
                                    </tr>
                                    {PURUSHAS.map((p: { id: Purusha; gloss: string }) => (
                                        <tr key={p.id}>
                                            <THead className="person">{p.gloss}</THead>
                                            {VACANAS.map((v: { id: Vacana }) => (
                                                <Cell
                                                    key={v.id}
                                                    forms={table.cells[p.id][v.id]}
                                                    onSelect={setSelected}
                                                    selected={selected}
                                                />
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        )}

                        <Analysis>
                            {GANA_LABEL[current.gana]}
                            {current.artha ? ` · ${current.artha}` : ""}
                        </Analysis>

                        {props.onDecline &&
                            <OtherParadigm type="button" onClick={props.onDecline}>
                                {props.word} &mdash; decline
                            </OtherParadigm>}

                        {selected && <Prakriya form={selected} onClose={() => setSelected(undefined)} />}
                    </>
                )}

                {!roots && <Loading>…</Loading>}
            </StyledWindow>
        );
    },
);

const StyledWindow = styled.div`
  position: fixed;
  text-align: center;
  z-index: 1000;
  top: 50%;
  left: 50%;
  min-width: 300px;
  max-width: min(620px, 94vw);
  max-height: 90vh;
  overflow-y: auto;
  min-height: 260px;
  transform: translate(-50%, -50%);
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.gray};
  padding: 5px;
  box-shadow: 0px 2px 2px rgb(0 0 0 / 30%);
`;

const Word = styled.div`
    text-align: center;
    font-weight: bold;
    margin-top: 15px;
`;

const Loading = styled.div`
    margin: 40px 0;
    opacity: 0.6;
`;

const Empty = styled.div`
    margin: 30px 10px;
    opacity: 0.7;
    font-size: 0.85em;
`;

const Analysis = styled.div`
    font-size: 0.7em;
    opacity: 0.75;
    margin: -10px 0 10px 0;
`;

/* A word can be both a root and a noun; this reaches the other paradigm. */
const OtherParadigm = styled.button`
    display: block;
    margin: 0 auto 12px auto;
    font: inherit;
    font-size: 0.7em;
    background: none;
    border: none;
    cursor: pointer;
    opacity: 0.75;
    text-decoration: underline dotted;
    &:hover { opacity: 1; }
`;

const RootChoice = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin: 10px 5px 0 5px;
`;

const RootButton = styled.button`
    font: inherit;
    font-size: 0.7em;
    cursor: pointer;
    padding: 2px 6px;
    border: 1px solid ${(props) => props.theme.colors.lightgray};
    background: none;
    border-radius: 3px;

    &[data-selected="true"] {
        background: ${(props) => props.theme.colors.pink};
        font-weight: bold;
    }
`;

const LakaraChoice = styled(RootChoice)`
    margin-top: 8px;
`;

const LakaraButton = styled(RootButton)``;

const StyledTable = styled.table`
  font-size: 0.7em;
  margin: 12px auto 20px auto;
  width: 100%;

  td { padding: 3px; white-space: nowrap; }
`;

const THead = styled.td`
    font-weight: bold;
    text-transform: capitalize;
`;

const CloseButton = styled.a`
  position: absolute;
  right: 20px;
  top: 13px;
  display: block;
  font-weight: bold;
  cursor: pointer;
`;
